import { createHashHistory, createBrowserHistory, createMemoryHistory } from 'history';
import { isTrue } from 'util/general';

const parseBool = (val) => {
  const str = `${val || ''}`;
  switch (str.toLowerCase()) {
    case '1':
    case 't':
    case 'true':
      return true;
    default:
      return false;
  }
};

/**
 * Env holds access to environment variables for the image and environment.
 * This attempts to pull data from the environment to build out.
 * All process.env calls are replaced on webpack build with the environment variables at build time
 * Placeholders <Something> during the build are replaced with the live environment data in the entrypoint.sh
 * when the docker image launches.
 */
class Env {
  /**
   *
   * @param {string} envString
   * @returns
   */
  CleanEnv(envString) {
    let result = envString;
    if (result && ((result.startsWith('"') && result.endsWith('"')) || (result.startsWith('\'') && result.endsWith('\'')))) {
      result = result.substring(1, result.length - 1);
    }
    return result;
  }

  History() {
    if (!this.history) {
      const useHashRouting = parseBool(process.env.HASH_ROUTING);
      if (useHashRouting) {
        this.history = createHashHistory();
      } else if (this.IsTest()) {
        this.history = createMemoryHistory();
      } else {
        this.history = createBrowserHistory();
      }
    }
    return this.history;
  }

  ApiBaseURL() {
    const url = process.env.API_BASE_URL || '';

    if (url === '' && window) {
      const loc = window.location || {};
      const protocol = loc.protocol || 'http:';
      const hostname = loc.hostname || 'localhost';
      const { port } = loc;
      return `${protocol}//${hostname}${port ? `:${port}` : ''}/api`;
    }

    return url;
  }

  IsPWTest() {
    return !this.IsProduction() && window.IS_PLAYWRIGHT === true;
  }

  IsProduction() {
    return process.env.NODE_ENV === 'production';
  }

  IsTest() {
    return process.env.NODE_ENV === 'test';
  }

  IsDevelopment() {
    return process.env.NODE_ENV === 'development';
  }

  IsStaging() {
    return process.env.NODE_ENV === 'staging';
  }

  BasicAuthenticationEnabled() {
    return parseBool(process.env.BASIC_ENABLED);
  }

  SamlEnabled() {
    return parseBool(process.env.SAML_ENABLED);
  }

  Saml2Enabled() {
    return parseBool(process.env.SAML2_ENABLED);
  }

  SamlIdp1Name() {
    return process.env.SAML_IDP1_NAME;
  }

  SamlIdp2Name() {
    return process.env.SAML_IDP2_NAME;
  }

  SamlSLOEnabled() {
    return parseBool(process.env.SAML_SLO_ENABLED);
  }

  SamlLogoutEnabled() {
    return parseBool(process.env.SAML_LOGOUT_ENABLED);
  }

  SamlLogoutURL() {
    return process.env.SAML_LOGOUT_URL || '';
  }

  SessionTimeout() {
    return parseInt(process.env.SESSION_TIMEOUT || '10', 10);
  }

  DisableDraftPDF() {
    return isTrue(process.env.DISABLE_DRAFT_PDF);
  }

  LoginAnnouncementTitle() {
    let result = process.env.LOGIN_ANNOUNCEMENT_TITLE || '__LOGIN_ANNOUNCEMENT_TITLE__';
    result = this.CleanEnv(result);
    return result;
  }

  LoginAnnouncementBody() {
    let result = process.env.LOGIN_ANNOUNCEMENT_BODY || '__LOGIN_ANNOUNCEMENT_BODY__';
    result = this.CleanEnv(result);
    return result;
  }

  SurveyUrl() {
    let result = process.env.SURVEY_URL || '__SURVEY_URL__';
    result = this.CleanEnv(result);
    return result;
  }

  AttachmentsEnabled() {
    return parseBool(process.env.ATTACHMENTS_ENABLED);
  }

  FileMaximumSize() {
    return parseInt(process.env.FILE_MAXIMUM_SIZE || '5000000', 10);
  }

  FileTypes() {
    return (process.env.FILE_TYPES || '.tiff;.png;.pdf').split(';').map((x) => x.replace('.', '').toUpperCase());
  }

  EndpointBasicAuthentication() {
    return '/auth/basic';
  }

  EndpointIdentityStatus() {
    return '/me/identitystatus';
  }

  EndpointLogout() {
    return '/me/logout';
  }

  EndpointRefresh() {
    return '/me/refresh';
  }

  EndpointSaml() {
    return `${this.ApiBaseURL()}/auth/saml`;
  }

  EndpointSaml2() {
    return `${this.ApiBaseURL()}/auth/saml2`;
  }

  EndpointSamlSLO() {
    return `${this.ApiBaseURL()}/auth/saml_slo`;
  }

  EndpointSamlLogout() {
    return `${this.ApiBaseURL()}/auth/logout`;
  }

  EndpointSave() {
    return '/me/save';
  }

  EndpointSection(type) {
    return `/me/section?type=${type || ''}`;
  }

  EndpointSetAccount() {
    return '/me/setaccount';
  }

  EndpointStatus() {
    return '/me/status';
  }

  EndpointEnvironment() {
    return '/me/environment';
  }

  EndpointForm() {
    return '/me/form';
  }

  EndpointSubmit() {
    return '/me/form/submit';
  }

  EndpointFormHash() {
    return '/me/form/hash';
  }

  EndpointValidate() {
    return '/me/validate';
  }

  EndpointValidateLog() {
    return '/me/validatelog';
  }

  EndpointAttachment() {
    return '/me/attachment';
  }

  EndpointAttachmentUpdate(id) {
    return `/me/attachment/${id}`;
  }

  EndpointAttachmentGet(id) {
    return `/me/attachment/${id}`;
  }

  EndpointAttachmentDelete(id) {
    return `/me/attachment/${id}/delete`;
  }

  EndpointGetForm() {
    return '/me/pdf';
  }

  EndpointGetPDFStatus() {
    return '/me/pdf/status';
  }

  EndpointApiVersion() {
    return '/app/version';
  }

  EndpointDraftPdf() {
    return '/me/form/draftpdf';
  }

  EndpointGetServerTime() {
    return '/app/servertime';
  }

  EndpointGetInstituteSearch() {
    return '/me/institutionsearch';
  }

  EndpointGetInstituteNameSearch() {
    return '/me/institutionnamesearch';
  }

  EndpointGetInstituteAddressSearch() {
    return '/me/institutionaddresssearch';
  }

  EndpointCheckStatus() {
    return '/me/checkstatus';
  }

  EndpointIdentityVerification() {
    return '/me/verifyself';
  }

  EndpointGetAnnouncements() {
    return '/app/announcements';
  }
}

const env = new Env();
export default env;
